import React, { useState, useMemo } from "react";
import { OptimizedNumberCell, OptimizedColorCell } from "./OptimizedCell";
import { PledgePlusOrderComparisonModal } from "./PledgePlusOrderComparisonModal";
import { useGlobalData } from "../hooks/useGlobalHooks";
import apiClient from "../utils/apiClient";
import CancelConfirmationModal from "./CancelConfirmationModal";
import ExitSuccessModal from "./ExitSuccessModal";
import server from "../utils/serverConfig";
import PledgePlusBulkOrderModal from "./PledgePlusBulkOrderModal";
import BulkModifyModal from "./BulkModifyModal";
import GroupSelectionModal from "./GroupSelectionModal";

/**
 * Get broker badge info
 */
function getBrokerBadge(broker) {
    const brokerLower = broker?.toLowerCase() || "";
    if (brokerLower === "fyers") return { text: "FY", class: "bg-blue-800 text-white" };
    if (brokerLower === "dhan_hq") return { text: "DHQ", class: "bg-emerald-950 text-white" };
    if (brokerLower.includes("dhan")) return { text: "DH", class: "bg-emerald-800 text-white" };
    if (brokerLower === "zerodha") return { text: "ZE", class: "bg-[#EF5350] text-white" };
    if (brokerLower === "nubra") return { text: "NU", class: "bg-purple-800 text-white" };
    return { text: "??", class: "bg-gray-700 text-white" };
}

function formatNumber(num) {
    if (num === undefined || num === null) return "-";
    return num.toLocaleString("en-IN");
}

export function PledgePlusOrdersView({
    data,
    baseQtyMap,
    activeAssets,
    loading,
    symbolStrategyBaseQty,

    orders,
    positions // New Prop
}) {
    const { users, userMappings } = useGlobalData();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Exit");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkModifyOpen, setBulkModifyOpen] = useState(false);
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // Client Broker Map
    const clientBrokerMap = useMemo(() => {
        const map = {};
        if (users) {
            users.forEach(item => {
                map[item.client_id] = item.broker;
            });
        }
        return map;
    }, [users]);

    // Open Confirmation Modal
    const openCancelModal = (ordersGroup) => {
        setOrdersToCancel(ordersGroup);
        setConfirmModalOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) {
            setConfirmModalOpen(false);
            return;
        }

        try {
            setSuccessModalActionType("Cancel");
            const xtsOrders = [];
            const xtsAnuragOrders = [];
            const godseyeOrders = [];

            ordersToCancel.forEach(order => {
                const clientId = order.clientId || order.client_id;
                let broker = (order.broker || "").toLowerCase();
                
                if (!broker && clientBrokerMap && clientId) {
                    // Robust lookup in clientBrokerMap
                    const foundBroker = Object.entries(clientBrokerMap).find(([id]) => 
                        String(id).trim().toLowerCase() === String(clientId).trim().toLowerCase()
                    );
                    broker = (foundBroker?.[1] || "").toLowerCase();
                }
                
                if (!broker) broker = "fyers";

                const payloadOrder = {
                    orderid: String(order.order_id || order.orderId),
                    clientid: clientId,
                    broker: broker,
                };

                if (broker === "dhan_xts" || broker === "xts" || broker === "dhan_ss" || broker === "dhan") {
                    xtsOrders.push(payloadOrder);
                } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag" || broker === "dhan_anurag") {
                    xtsAnuragOrders.push(payloadOrder);
                } else {
                    godseyeOrders.push(payloadOrder);
                }
            });

            const promises = [];
            if (xtsOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan.baseUrl}manual-cancel-orders-xts`, xtsOrders)
                        .then(res => ({ type: 'xts', data: res.data }))
                        .catch(err => ({ type: 'xts', error: err }))
                );
            }
            if (xtsAnuragOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.ss_dhan_anurag.baseUrl}manual-cancel-orders-xts`, xtsAnuragOrders)
                        .then(res => ({ type: 'xts_anurag', data: res.data }))
                        .catch(err => ({ type: 'xts_anurag', error: err }))
                );
            }
            if (godseyeOrders.length > 0) {
                promises.push(
                    apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, godseyeOrders)
                        .then(res => ({ type: 'godseye', data: res.data }))
                        .catch(err => ({ type: 'godseye', error: err }))
                );
            }

            const results = await Promise.all(promises);
            let allSubmittedOrders = [];
            let errorMsg = "";

            results.forEach(res => {
                if (res.error) {
                    console.error(`❌ Error cancelling ${res.type} orders:`, res.error);
                    errorMsg += `${res.type.toUpperCase()} failed: ${res.error.response?.data?.detail || res.error.message}. `;
                } else if (res.data && res.data.orders) {
                    const responseOrders = res.data.orders.map(o => ({
                        ...o,
                        orderId: o.orderid,
                        symbol: ordersToCancel[0]?.symbol
                    }));
                    allSubmittedOrders = [...allSubmittedOrders, ...responseOrders];
                }
            });

            if (allSubmittedOrders.length > 0) {
                setSubmittedOrders(allSubmittedOrders);
                setSuccessModalOpen(true);
            }

            if (errorMsg) {
                if (allSubmittedOrders.length === 0) {
                    alert(`Failed to cancel orders: ${errorMsg}`);
                } else {
                    console.warn("Partial cancellation error:", errorMsg);
                }
            }

            setConfirmModalOpen(false);

        } catch (error) {
            console.error("❌ Unexpected error cancelling orders:", error);
            alert(`Unexpected error: ${error.message}`);
            setConfirmModalOpen(false);
        }
    };

    const handleBulkSuccess = (orders) => {
        setSuccessModalActionType("Placement");
        setSubmittedOrders(orders);
        setSuccessModalOpen(true);
    };

    const handleRemoveFromBatch = (orderId) => {
        setOrdersToCancel(prev => {
            const newOrders = prev.filter(o => {
                const oid = o.order_id || o.orderId || o.appOrderID;
                return String(oid) !== String(orderId);
            });
            if (newOrders.length === 0) {
                setConfirmModalOpen(false);
            }
            return newOrders;
        });
    };

    const cancelGroups = useMemo(() => {
        if (!orders || orders.length === 0) return { assets: {}, labels: {} };

        const cancellableOrders = orders.filter(o => {
            const oStatus = (o.status || "").toLowerCase();
            const isPending = oStatus === "pending" || oStatus === "open" || oStatus === "trigger pending" || oStatus === "not sent" || String(o.status_no) === "6";
            return isPending;
        });

        const assets = {};
        const labels = {};

        const labelMap = {};
        if (users) {
            users.forEach(c => labelMap[c.client_id] = c.label);
        }

        cancellableOrders.forEach(o => {
            const sym = (o.symbol || "").toUpperCase();
            const clientId = o.clientId || o.client_id;

            let assetKey = null;
            const sortedActiveAssets = [...(activeAssets || [])].sort((a, b) => b.length - a.length);
            for (const asset of sortedActiveAssets) {
                const cleanSym = sym.replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "");
                if (cleanSym.startsWith(asset)) {
                    assetKey = asset;
                    break;
                }
            }

            if (assetKey) {
                if (!assets[assetKey]) assets[assetKey] = [];
                assets[assetKey].push(o);
            }

            const label = labelMap[clientId];
            if (label) {
                if (!labels[label]) labels[label] = [];
                labels[label].push(o);
            }
        });

        return { assets, labels };
    }, [orders, activeAssets, users]);

    const handleOpenBulkModify = (orders) => {
        setOrdersToModify(orders);
        setBulkModifyOpen(true);
    };

    const handleOpenModal = (clientData) => {
        setSelectedClient(clientData);
        setModalOpen(true);
    };

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [searchQuery, setSearchQuery] = useState("");

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
        return sortConfig.direction === "asc" ?
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> :
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
    };

    const processedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        let processed = [...data];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            processed = processed.filter((row) => {
                return (
                    (row.broker || "").toLowerCase().includes(q) ||
                    (row.clientId || "").toLowerCase().includes(q) ||
                    (row.clientName || "").toLowerCase().includes(q)
                );
            });
        }

        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aValue, bValue;
                if (sortConfig.key.includes("_")) {
                    const [asset, prop] = sortConfig.key.split("_");
                    aValue = a[asset]?.[prop] ?? 0;
                    bValue = b[asset]?.[prop] ?? 0;
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (typeof aValue === "number" && typeof bValue === "number") {
                    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
                }
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                return sortConfig.direction === "asc" ? (aStr < bStr ? -1 : 1) : (aStr > bStr ? -1 : 1);
            });
        }
        return processed;
    }, [data, sortConfig, searchQuery]);

    const MCX_ASSETS = ["GOLD", "SILVER", "GOLDM", "SILVERM", "CRUDEOIL", "NATURALGAS", "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"];

    const isMCX = (asset) => {
        const upper = asset?.toUpperCase() || "";
        return MCX_ASSETS.some(a => upper.startsWith(a));
    };

    const sortedAssets = useMemo(() => {
        if (!activeAssets) return [];
        const nonMcx = activeAssets.filter(a => !isMCX(a));
        const mcx = activeAssets.filter(a => isMCX(a));
        return [...nonMcx, ...mcx];
    }, [activeAssets]);

    if (loading) {
        return (
            <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
                <p className="text-gray-400">Loading orders...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-3 mb-3 bg-[#14161f] border-b border-gray-800/50 flex-wrap gap-4">
                {/* Left Side: Info */}
                <div className="flex items-center gap-6 flex-wrap">
                    {/* Base Qty Info */}
                    {baseQtyMap && Object.keys(baseQtyMap).length > 0 && (
                        <div className="flex items-center gap-3">
                            <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider">Base Qty:</span>
                            <div className="flex items-center gap-2">
                                {Object.entries(baseQtyMap).map(([asset, qty]) => (
                                    <span key={asset} className="bg-[#0f1116] border border-gray-800 px-2.5 py-1 rounded-md text-xs text-gray-300 shadow-inner">
                                        <span className="font-bold text-blue-400">{asset}:</span> {formatNumber(qty)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Side: Actions */}
                <div className="flex items-center gap-4 flex-wrap ml-auto">
                    {/* Cancel Buttons Section */}
                    {(Object.keys(cancelGroups.assets).length > 0 || Object.keys(cancelGroups.labels).length > 0) && (
                        <div className="flex items-center gap-2 pr-2">
                            {/* Asset Wise */}
                            {Object.entries(cancelGroups.assets).map(([asset, assetOrders]) => (
                                <button
                                    key={asset}
                                    onClick={() => openCancelModal(assetOrders)}
                                    className="px-3 py-1.5 bg-red-900/20 hover:bg-red-800/40 text-red-400 text-[10px] font-bold rounded border border-red-800/30 transition-all flex items-center gap-2 uppercase tracking-wide hover:shadow-lg hover:shadow-red-900/20"
                                >
                                    <span>Cancel {asset}</span>
                                    <span className="bg-red-950/50 px-1.5 rounded text-[10px]">{assetOrders.length}</span>
                                </button>
                            ))}

                            {/* Label Wise */}
                            {Object.entries(cancelGroups.labels).map(([label, labelOrders]) => (
                                <button
                                    key={label}
                                    onClick={() => openCancelModal(labelOrders)}
                                    className="px-3 py-1.5 bg-orange-900/20 hover:bg-orange-800/40 text-orange-400 text-[10px] font-bold rounded border border-orange-800/30 transition-all flex items-center gap-2 uppercase tracking-wide hover:shadow-lg hover:shadow-orange-900/40"
                                >
                                    <span>CANCEL {label}</span>
                                    <span className="bg-orange-950/50 px-1.5 rounded text-[10px]">{labelOrders.length}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Divider between cleanup and primary actions */}
                    {(Object.keys(cancelGroups.assets).length > 0 || Object.keys(cancelGroups.labels).length > 0) && (
                        <div className="w-px h-6 bg-gray-700/50 hidden sm:block"></div>
                    )}

                    <div className="flex items-center gap-3">
                        {/* Bulk Modify Button (Unified) */}
                        <button
                            onClick={() => setIsSelectionModalOpen(true)}
                            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-indigo-900/40 transition-all flex items-center gap-2 border border-indigo-400/30"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Bulk Entry Order Modify</span>
                        </button>

                        <button
                            onClick={() => setBulkModalOpen(true)}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-blue-900/40 transition-all flex items-center gap-2 border border-blue-400/30"
                        >
                            <span>Order Placement</span>
                        </button>
                    </div>
                </div>
            </div>

            {(!data || data.length === 0) ? (
                <div className="flex-1 flex flex-col items-center justify-center p-12 bg-[#1a1d29]/50">
                    <div className="text-center max-w-sm">
                        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/20">
                            <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                        </div>
                        <h3 className="text-white text-lg font-bold mb-2">No Order Data</h3>
                        <p className="text-gray-400 text-sm mb-6">
                            No orders found for the current selection. Use the button above to place new orders.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="Search broker / client id..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-[#0f1116] border border-gray-700 text-gray-200 text-sm px-3 py-1.5 rounded w-64 ml-6 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>

                    {/* Custom Scrollbar Styles */}
                    <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>

                    <div className="flex-1 overflow-auto sleek-scrollbar">
                        <table className="w-full text-sm border-collapse">
                            <thead className="sticky top-0 bg-[#14161f] z-10">
                                <tr className="border-b border-gray-700/50">
                                    <th rowSpan="2" className="text-center py-2 text-gray-400 font-medium bg-[#14161f] border-r border-gray-700/50 w-10">No.</th>
                                    <th rowSpan="2" onClick={() => handleSort("clientName")} className="text-left py-2 px-2 text-gray-300 font-medium bg-[#14161f] border-r border-gray-700/50 min-w-[180px] cursor-pointer hover:bg-[#1a1d29]">
                                        <div className="flex items-center gap-1"><span>Name</span>{getSortIcon("clientName")}</div>
                                    </th>
                                    {sortedAssets.map(asset => (
                                        <th key={asset} colSpan="2" onClick={() => handleSort(asset)} className="text-center py-2 px-2 text-white font-semibold border-l border-gray-700/50 cursor-pointer hover:bg-[#1a1d29]">
                                            <div className="flex items-center justify-center gap-1"><span>{asset}</span>{getSortIcon(asset)}</div>
                                        </th>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-700/50">
                                    {sortedAssets.map(asset => (
                                        <React.Fragment key={asset}>
                                            <th onClick={() => handleSort(`${asset}_multiplier`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/50 w-10 cursor-pointer hover:bg-[#1a1d29]">Mul</th>
                                            <th onClick={() => handleSort(`${asset}_qty`)} className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/30 w-20 cursor-pointer hover:bg-[#1a1d29]">QTY</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {processedData.map((row, idx) => (
                                    <tr key={idx} className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors">
                                        <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50">{idx + 1}</td>
                                        <td className="py-1.5 px-2 text-white font-medium border-r border-gray-700/50">
                                            <div className="flex justify-between items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class}`}>{getBrokerBadge(row.broker).text}</span>
                                                    <span>{row.clientName} <span className="text-xs text-gray-500">({row.clientId})</span>
                                                    {(() => {
                                                        const clientUser = users?.find(u => u.client_id === row.clientId || u.clientId === row.clientId);
                                                        const isPPMini = clientUser?.is_pledgeplus_mini === true || String(clientUser?.is_pledgeplus_mini).toLowerCase() === 'true';
                                                        return isPPMini ? <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-blue-900 text-blue-200 rounded">P+ Mini</span> : null;
                                                    })()}
                                                    </span>
                                                </div>
                                                <button onClick={() => handleOpenModal(row)} className="text-blue-400 hover:text-blue-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></button>
                                            </div>
                                        </td>
                                        {sortedAssets.map(asset => {
                                            const cellData = row[asset];
                                            if (!cellData) {
                                                return <React.Fragment key={asset}>
                                                    <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">-</td>
                                                    <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">-</td>
                                                </React.Fragment>
                                            }
                                            return <React.Fragment key={asset}>
                                                <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50"><OptimizedNumberCell value={cellData.multiplier} className="text-center text-gray-300" /></td>
                                                <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                                                    {cellData.buyQty > 0 && cellData.sellQty > 0 ? (
                                                        <div className="flex flex-col gap-0.5 text-[10px] leading-tight">
                                                            <span className="text-green-400 font-bold">B: {formatNumber(cellData.buyQty)}</span>
                                                            <span className="text-red-400 font-bold">S: {formatNumber(cellData.sellQty)}</span>
                                                        </div>
                                                    ) : cellData.buyQty > 0 ? (
                                                        <OptimizedColorCell value={cellData.buyQty} color="green" formatFn={formatNumber} className="text-center font-medium text-green-400" applyColorToCell={false} />
                                                    ) : (
                                                        <OptimizedColorCell value={cellData.sellQty} color="red" formatFn={formatNumber} className="text-center font-medium text-red-500" applyColorToCell={false} />
                                                    )}
                                                </td>
                                            </React.Fragment>
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <PledgePlusOrderComparisonModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                clientData={selectedClient}
                orders={orders}
                positions={positions}
                symbolStrategyBaseQty={symbolStrategyBaseQty}
            />

            <PledgePlusBulkOrderModal
                isOpen={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                users={users}
                userMappings={userMappings}
                onSuccess={handleBulkSuccess}
                existingOrders={orders}
                positions={positions}
            />

            <CancelConfirmationModal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleConfirmCancel}
                orders={ordersToCancel}
                onDelete={handleRemoveFromBatch}
            />

            <ExitSuccessModal
                isOpen={successModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                orders={submittedOrders}
                actionType={successModalActionType}
            />

            <BulkModifyModal
                isOpen={bulkModifyOpen}
                onClose={() => setBulkModifyOpen(false)}
                orders={ordersToModify}
                onSuccess={() => {/* Manual refetch handled by parent hook if needed */ }}
            />

            <GroupSelectionModal
                isOpen={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                orders={orders}
                onConfirm={handleOpenBulkModify}
                assetFilterType="pledgePlus"
                positions={positions}
            />
        </div>
    );
}
